import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ErrorIcon from '@mui/icons-material/Error'
import CompanyPaymentsTableRow from './CompanyPaymentsTableRow'
import { useSnackbar } from '../../../providers/SnackbarProvider'
import { FC, memo, useEffect } from 'react'
import { AppIcon } from '../../../atoms/AppIcon'
import { useCompanyPayments } from '../../../providers/CompanyPaymentsProvider'
import { InfiniteScroll } from '../../../atoms/InfiniteScroll'
import LinearProgress from '../../../atoms/LinearProgress'
import CircularProgress from '../../../atoms/CircularProgress'

const CompanyPaymentsTable: FC<{ scrollContainerRef?: React.RefObject<Element> }> = ({
  scrollContainerRef,
}) => {
  const { status, companyPayments, error, loadMore } = useCompanyPayments()
  const displayTable = status === 'loading-more' || status === 'error-more' || status === 'idle'
  const displayLoadingPanel = status === 'loading-initial'
  const displayEmptyPanel = status === 'empty'
  const displayErrorPanel = status === 'error-initial'
  const displayLoadingMore = status === 'loading-more'
  const { showError } = useSnackbar()
  useEffect(() => {
    if (status === 'error-more' && error) {
      showError(error)
    }
  }, [error, showError, status])
  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          // boxShadow: '0 0 35px 0 rgba(154, 161, 171, .50)',
          borderRadius: 1,
          backgroundColor: 'white',

          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: 2,
          }}
        >
          <Typography
            variant="h6"
            component={'span'}
            color="textPrimary"
            sx={{ textTransform: 'uppercase' }}
          >
            Invoices
          </Typography>
        </Box>

        {displayLoadingPanel && (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              padding: 4,
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <CircularProgress delay={200} />
          </Box>
        )}
        {displayErrorPanel && (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              padding: 4,
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',

              gap: 2,
            }}
          >
            <ErrorIcon color="error" />
            <Typography variant="body2" component={'span'} color="textSecondary">
              {error}
            </Typography>
          </Box>
        )}
        {displayEmptyPanel && (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              padding: 4,
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <AppIcon sx={{ width: 60, opacity: 0.5 }} />
            <Typography variant="body2" component={'span'} color="textSecondary">
              Invoices will be displayed here
            </Typography>
          </Box>
        )}
        {displayTable && (
          <InfiniteScroll
            sx={{ padding: 2 }}
            loadMore={loadMore}
            scrollContainerRef={scrollContainerRef}
          >
            <TableContainer component={'div'}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell size="small">Date</TableCell>
                    <TableCell align="left">Credits purchased</TableCell>
                    <TableCell align="right">Amount paid</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companyPayments.map(companyPayment => {
                    return (
                      <CompanyPaymentsTableRow
                        key={companyPayment.id}
                        companyPayment={companyPayment}
                      />
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </InfiniteScroll>
        )}
        {displayLoadingMore && (
          <Box sx={{ position: 'relative', height: 10 }}>
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
              <LinearProgress delay={200} />
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}

export default memo(CompanyPaymentsTable)
